import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { clientEfFoutTekst } from '../../utils/clientAanmaakFouten.js';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

function calcEndDate(membership) {
  if (!membership) return null;
  if (membership.end_date) return membership.end_date;
  if (membership.initial_end_date) return membership.initial_end_date;
  if (membership.start_date && membership.initial_duration_months) {
    const d = new Date(membership.start_date + 'T00:00:00');
    d.setDate(d.getDate() + membership.initial_duration_months * 31);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

function NieuweClientModal({ onClose, onCreated }) {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin_coach' || role === 'superadmin';
  const [voornaam, setVoornaam] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [coachId, setCoachId] = useState(isAdmin ? '' : (user?.id || ''));
  const [clientType, setClientType] = useState('active');
  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [coaches, setCoaches] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [homeLocationId, setHomeLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stuurWelkomstmail, setStuurWelkomstmail] = useState(true);
  const [createdResult, setCreatedResult] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [coachesRes, typesRes, locsRes] = await Promise.all([
          supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach', 'superadmin']).is('deleted_at', null).order('full_name'),
          supabase.from('membership_types').select('id, name, is_active').eq('is_active', true).order('name'),
          supabase.from('locations').select('id, name').eq('is_active', true).order('name'),
        ]);
        if (coachesRes.error) throw coachesRes.error;
        if (typesRes.error) throw typesRes.error;
        setCoaches(coachesRes.data || []);
        setMembershipTypes(typesRes.data || []);
        setLocations(locsRes.data || []);
        if (isAdmin && coachesRes.data?.length === 1) {
          setCoachId(coachesRes.data[0].id);
        }
      } catch (err) {
        console.error('Error loading coaches/types:', err);
      }
    }
    loadData();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!voornaam.trim()) { setError(t('clients.validatieVoornaam')); return; }
    if (!achternaam.trim()) { setError(t('clients.validatieAchternaam')); return; }
    if (!email.trim()) { setError(t('clients.validatieEmail')); return; }
    if (!coachId) { setError(t('clients.validatieCoach')); return; }
    if (clientType === 'active' && !membershipTypeId) { setError(t('clients.validatieAbonnement')); return; }

    setSaving(true);
    try {
      const fullName = `${voornaam.trim()} ${achternaam.trim()}`;

      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession?.access_token) throw new Error(t('clients.geenSessie'));

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshSession.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          full_name: fullName,
          phone: phone.trim() || null,
          coach_id: coachId,
          client_status: clientType,
          membership_type_id: clientType === 'active' ? membershipTypeId : null,
          membership_start_date: clientType === 'active' ? startDate : null,
          street: street.trim() || null,
          house_number: houseNumber.trim() || null,
          postal_code: postalCode.trim() || null,
          city: city.trim() || null,
          home_location_id: homeLocationId || null,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        // Foutcodes van create-client vertalen naar een begrijpelijke melding
        // (i18n), inclusief de expliciete waarschuwing als de rollback van de
        // auth-gebruiker mislukte en het e-mailadres dus bezet blijft.
        throw new Error(clientEfFoutTekst(result));
      }

      const newClientId = result.user_id || result.client_id || result.id;

      // Stuur welkomstmail als checkbox aangevinkt — het server-gegenereerde
      // wachtwoord gaat mee zodat de client daarmee kan inloggen.
      let emailed = false;
      if (stuurWelkomstmail && newClientId) {
        try {
          const { data: { session: sess2 } } = await supabase.auth.getSession();
          if (sess2?.access_token) {
            const mailResp = await fetch(`${supabase.supabaseUrl}/functions/v1/send-welcome-email`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${sess2.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ client_id: newClientId, password: result.password }),
            });
            emailed = mailResp.ok;
          }
        } catch (mailErr) {
          console.warn('Welkomstmail kon niet worden verstuurd:', mailErr);
        }
      }

      await onCreated();
      // Wachtwoord wordt maar één keer getoond (het staat nergens anders meer
      // opgeslagen) — de coach moet het eerst zien/noteren vóór de modal dicht mag.
      // De proeftraining-vraag verschijnt pas ná het sluiten van dit scherm
      // (zie onClose hieronder) zodat de twee modals nooit tegelijk zichtbaar zijn.
      setCreatedResult({ id: newClientId, password: result.password, emailed, name: fullName });
    } catch (err) {
      setError(t('clients.foutAanmaken') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (createdResult) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 480 }}>
          <div className="modal-header">
            <h2 className="modal-title">{t('clients.wachtwoordTonenTitel', { naam: createdResult.name })}</h2>
          </div>
          <p>{t('clients.wachtwoordTonenUitleg')}</p>
          <div className="form-group">
            <label className="form-label">{t('clients.labelWachtwoord')}</label>
            <input className="form-input" readOnly value={createdResult.password} style={{ fontFamily: 'monospace', fontSize: 16 }} onFocus={e => e.target.select()} />
          </div>
          <p className="text-muted" style={{ fontSize: 13 }}>
            {createdResult.emailed ? t('clients.wachtwoordTonenGemaild') : t('clients.wachtwoordTonenNietGemaild')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-primary" onClick={() => onClose(createdResult)}>
              {t('clients.wachtwoordTonenSluiten')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('clients.modalTitelNieuw')}</h2>
          <button className="modal-close" onClick={() => onClose()}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelVoornaam')} *</label>
              <input
                className="form-input"
                value={voornaam}
                onChange={e => setVoornaam(e.target.value)}
                placeholder={t('clients.placeholderVoornaam')}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelAchternaam')} *</label>
              <input
                className="form-input"
                value={achternaam}
                onChange={e => setAchternaam(e.target.value)}
                placeholder={t('clients.placeholderAchternaam')}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('clients.labelEmail')} *</label>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('clients.placeholderEmail')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clients.labelPhone')}</label>
            <input
              className="form-input"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={t('clients.placeholderPhone')}
            />
          </div>

          {isAdmin && (
            <div className="form-group">
              <label className="form-label">{t('clients.labelCoach')} *</label>
              <select
                className="form-input"
                value={coachId}
                onChange={e => setCoachId(e.target.value)}
              >
                <option value="">{t('clients.selecteerCoach')}</option>
                {coaches.length === 0 && <option disabled>{t('clients.geenCoachesGevonden')}</option>}
                {coaches.map(c => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('clients.labelType')} *</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="clientType"
                  value="active"
                  checked={clientType === 'active'}
                  onChange={e => setClientType(e.target.value)}
                  style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                />
                {t('clients.typeActief')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="clientType"
                  value="prospect"
                  checked={clientType === 'prospect'}
                  onChange={e => setClientType(e.target.value)}
                  style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                />
                {t('clients.typeProspect')}
              </label>
            </div>
          </div>

          {clientType === 'active' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('clients.labelAbonnement')} *</label>
                <select
                  className="form-input"
                  value={membershipTypeId}
                  onChange={e => setMembershipTypeId(e.target.value)}
                >
                  <option value="">{t('clients.selecteerAbonnement')}</option>
                  {membershipTypes.length === 0 && <option disabled>{t('clients.geenAbonnementenGevonden')}</option>}
                  {membershipTypes.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('clients.labelStartdatum')}</label>
                <input
                  className="form-input"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 12 }}>{t('clients.sectionAdres')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelStraat')}</label>
                <input className="form-input" value={street} onChange={e => setStreet(e.target.value)} placeholder={t('clients.placeholderStraat')} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelHuisnummer')}</label>
                <input className="form-input" value={houseNumber} onChange={e => setHouseNumber(e.target.value)} placeholder={t('clients.placeholderHuisnummer')} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelPostcode')}</label>
                <input className="form-input" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder={t('clients.placeholderPostcode')} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelWoonplaats')}</label>
                <input className="form-input" value={city} onChange={e => setCity(e.target.value)} placeholder={t('clients.placeholderWoonplaats')} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelStandaardLocatie')}</label>
              <select className="form-input" value={homeLocationId} onChange={e => setHomeLocationId(e.target.value)}>
                <option value="">{t('clients.geenLocatie')}</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ padding: '12px 0', borderTop: '1px solid var(--color-border)', marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={stuurWelkomstmail}
                onChange={e => setStuurWelkomstmail(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
              />
              <span style={{ fontSize: 14 }}>{t('welkomstmail.checkboxLabel')}</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose()} disabled={saving}>
              {t('clients.annuleren')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('auth.loading') : t('clients.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TrialPopupModal({ client, onClose, onKiesPT, onKiesGroepsles }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('clients.trialPopupTitel')}</h2>
        </div>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20 }}>
          {t('clients.trialPopupUitleg').replace('{naam}', client.name)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn btn-primary" onClick={onKiesPT} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupJaPT')}
          </button>
          <button className="btn btn-primary" onClick={onKiesGroepsles} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupJaGroepsles')}
          </button>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupNee')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'prospect' | 'inactive'
  const [showModal, setShowModal] = useState(false);
  const [trialPopupClient, setTrialPopupClient] = useState(null);
  const { showSuccess } = useToast();

  useEffect(() => {
    load();
  }, []);

  function getClientStatus(c) {
    // Prospect heeft voorrang
    if (c.client_status === 'prospect') return 'prospect';
    // Anders kijken naar lidmaatschap
    const today = new Date().toISOString().slice(0, 10);
    const endDate = calcEndDate(c.membership);
    const isActive = c.membership && (endDate === null || endDate >= today);
    return isActive ? 'active' : 'inactive';
  }

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
    if (!matchesSearch) return false;
    if (statusFilter === 'all') return true;
    return getClientStatus(c) === statusFilter;
  });

  async function handleClientCreated() {
    await load();
    showSuccess(t('clients.succesBericht'));
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data: profiles, error: profilesErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, customer_number, client_status, deleted_at')
        .eq('role', 'client')
        .order('full_name', { ascending: true });

      if (profilesErr) throw profilesErr;

      if (!profiles || profiles.length === 0) {
        setClients([]);
        return;
      }

      const clientIds = profiles.map(p => p.id).filter(Boolean);
      const today = new Date().toISOString().slice(0, 10);

      const [membRes, ccRes] = await Promise.all([
        clientIds.length > 0
          ? supabase
              .from('client_memberships')
              .select('client_id, current_credits, start_date, end_date, initial_end_date, initial_duration_months, status, membership_types(name)')
              .in('client_id', clientIds)
              .eq('status', 'active')
              .order('start_date', { ascending: false })
          : Promise.resolve({ data: [] }),
        supabase
          .from('coach_clients')
          .select('client_id, coach_id, is_primary')
          .eq('is_primary', true)
          .in('client_id', clientIds),
      ]);

      if (membRes.error) throw membRes.error;

      const membMap = {};
      (membRes.data || []).forEach(m => {
        if (!membMap[m.client_id]) membMap[m.client_id] = m;
      });

      // Haal coachnamen apart op om RLS-afhankelijkheid op nested join te vermijden
      const coachIds = [...new Set((ccRes.data || []).map(cc => cc.coach_id).filter(Boolean))];
      let coachNamesMap = {};
      if (coachIds.length > 0) {
        const { data: coachProfiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', coachIds);
        (coachProfiles || []).forEach(cp => { coachNamesMap[cp.id] = cp.full_name; });
      }

      const coachMap = {};
      (ccRes.data || []).forEach(cc => {
        if (coachNamesMap[cc.coach_id]) coachMap[cc.client_id] = coachNamesMap[cc.coach_id];
      });

      setClients(profiles.map(p => ({
        ...p,
        membership: membMap[p.id] || null,
        coachName: coachMap[p.id] || null,
      })));
    } catch (err) {
      setError(t('clients.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {showModal && (
        <NieuweClientModal
          onClose={(created) => {
            setShowModal(false);
            // Pas ná het sluiten van het wachtwoordscherm de proeftraining-vraag
            // tonen, nooit gelijktijdig (zie Onderdeel 1, sessie 2026-07-28).
            if (created?.id) setTrialPopupClient({ id: created.id, name: created.name });
          }}
          onCreated={handleClientCreated}
        />
      )}

      {trialPopupClient && (
        <TrialPopupModal
          client={trialPopupClient}
          onClose={() => setTrialPopupClient(null)}
          onKiesPT={() => {
            const client = trialPopupClient;
            setTrialPopupClient(null);
            navigate('/coach/les/nieuw', {
              state: {
                prefillPT: true,
                type: 'personal',
                clientId: client.id,
                coachId: user.id,
                clientName: client.name,
                prefillTrial: true,
              },
            });
          }}
          onKiesGroepsles={() => {
            const client = trialPopupClient;
            setTrialPopupClient(null);
            navigate('/coach/rooster', {
              state: {
                filterType: 'group',
                trialClientId: client.id,
                trialClientName: client.name,
              },
            });
          }}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('clients.title')}</h1>
          <p className="page-subtitle">{filtered.length} {t('clients.aantalGevonden')}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            {t('clients.toevoegenKnop')}
          </button>
          <input
            type="search"
            className="form-input"
            placeholder={t('clients.zoekPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 0, maxWidth: 260 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {['all', 'active', 'prospect', 'inactive'].map(f => (
          <button
            key={f}
            className={statusFilter === f ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setStatusFilter(f)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {t(`clients.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clients.colNaam')}</th>
                  <th>{t('clients.colKlantnummer')}</th>
                  <th>{t('clients.colEmail')}</th>
                  <th>{t('clients.colCoach')}</th>
                  <th>{t('clients.colLidmaatschap')}</th>
                  <th>{t('clients.colGeldigTm')}</th>
                  <th>{t('clients.colCredits')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="empty-state">
                        <div style={{ fontSize: 32 }}>👥</div>
                        <p>{search ? t('clients.geenResultaatZoek') : t('clients.geenClinten')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(c => {
                    const clientStatus = getClientStatus(c);
                    const active = clientStatus === 'active';
                    return (
                      <tr key={c.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="font-medium">{c.full_name || '—'}</span>
                            {c.deleted_at && (
                              <span style={{
                                background: '#6b728022',
                                color: '#6b7280',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                              }}>
                                {t('clients.statusVerwijderd')}
                              </span>
                            )}
                            {!c.deleted_at && clientStatus === 'prospect' && (
                              <span style={{
                                background: '#fbbf2422',
                                color: '#d97706',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                              }}>
                                {t('clients.statusProspect')}
                              </span>
                            )}
                            {!c.deleted_at && clientStatus === 'inactive' && (
                              <span style={{
                                background: '#ef444422',
                                color: '#dc2626',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                              }}>
                                {t('clients.statusInactive')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-muted" style={{ fontSize: 12 }}>{c.customer_number || '—'}</td>
                        <td className="text-muted">{c.email || '—'}</td>
                        <td className="text-muted">{c.coachName || '—'}</td>
                        <td>
                          {c.membership ? (
                            <span className={`badge ${active ? 'badge-active' : 'badge-inactive'}`}>
                              {c.membership.membership_types?.name || t('clients.onbekend')}
                            </span>
                          ) : (
                            <span className="badge badge-inactive">{t('clients.geen')}</span>
                          )}
                        </td>
                        <td className="text-muted">
                          {fmtDate(calcEndDate(c.membership))}
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                            {c.membership?.current_credits ?? '—'}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/coach/clients/${c.id}`}
                            className="btn btn-secondary btn-sm"
                          >
                            {t('clients.details')}
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
